import { ROLES } from '../constants/constants';

const ROLE_GROUPS = {
  admin: [ROLES.Admin, ROLES.MarketAdmin, ROLES.CountryAdmin],
  pma: [ROLES.Pma, ROLES.PmaGM],
  retailer: [ROLES.Retailer, ROLES.RetailerGM],
};

const HAC_MANAGEMENT_ROLES = [
  ...ROLE_GROUPS.admin,
  ...ROLE_GROUPS.pma,
  ...ROLE_GROUPS.retailer,
];

const HAC_STAFF_ROLES = [
  ...ROLE_GROUPS.admin,
  ...ROLE_GROUPS.pma,
];

const LIBRARY_ALLOWED_ROLES = [
  ...ROLE_GROUPS.admin,
  ROLES.Translator,
  ROLES.Reviewer,
];

const PRE_PUBLISH_ALLOWED_ROLES = LIBRARY_ALLOWED_ROLES;
const PRO_PUBLISH_ALLOWED_ROLES = [...ROLE_GROUPS.admin, ROLES.Reviewer];
const LIBRARY_UPLOAD_ALLOWED_ROLES = [...ROLE_GROUPS.admin, ROLES.Translator, ROLES.Reviewer];

// Translations features (Library, Translators submenu) are hidden in PRO; only PRE/STAGING/LOCAL show them
const isTranslationsFeatureEnvironment = () => process.env.REACT_APP_ENV_NAME !== 'PRO';

const isAuthorizedRole = (role, allowedRoles) => allowedRoles.includes(role);
const isAdminRole = (role) => isAuthorizedRole(role, ROLE_GROUPS.admin);
const isPmaRole = (role) => isAuthorizedRole(role, ROLE_GROUPS.pma);
const isPmaManagerRole = (role) => role === ROLES.PmaGM;
const isRetailerRole = (role) => isAuthorizedRole(role, ROLE_GROUPS.retailer);
const canAccessHacManagement = (role) => isAuthorizedRole(role, HAC_MANAGEMENT_ROLES);
const canAccessHacStaffArea = (role) => isAuthorizedRole(role, HAC_STAFF_ROLES);
const canViewUsers = (role) => isAuthorizedRole(role, ROLE_GROUPS.admin);
const canAccessLibrary = (role) =>
  isAuthorizedRole(role, LIBRARY_ALLOWED_ROLES) && isTranslationsFeatureEnvironment();
const canAccessTranslators = (role) =>
  isAuthorizedRole(role, ROLE_GROUPS.admin) && isTranslationsFeatureEnvironment();
const canAccessClientPreview = (role) => isAuthorizedRole(role, LIBRARY_ALLOWED_ROLES);
const canPublishLibraryToPre = (role) =>
  isAuthorizedRole(role, PRE_PUBLISH_ALLOWED_ROLES);
const canPublishLibraryToPro = (role) =>
  isAuthorizedRole(role, PRO_PUBLISH_ALLOWED_ROLES);
const canUploadLibraryFile = (role) =>
  isAuthorizedRole(role, LIBRARY_UPLOAD_ALLOWED_ROLES);
const canCreateUser = (role) => isAuthorizedRole(role, ROLE_GROUPS.admin);
const canSeeReports = (role) => isAuthorizedRole(role, ROLE_GROUPS.admin);

const getHacBackPathForRole = (role, { retailerStatus = 'active', pmaOrigin } = {}) => {
  if (isAdminRole(role)) {
    return `/${ROLES.Admin}/main`;
  }

  if (isRetailerRole(role)) {
    return `/${ROLES.Retailer}/main/${retailerStatus}`;
  }

  if (isPmaRole(role)) {
    return pmaOrigin === 'calendar'
      ? `/${ROLES.Pma}/main`
      : `/${ROLES.Pma}/main/hac`;
  }

  return `/${role}/main`;
};

export {
  ROLE_GROUPS,
  HAC_MANAGEMENT_ROLES,
  HAC_STAFF_ROLES,
  isAuthorizedRole,
  isAdminRole,
  isPmaRole,
  isPmaManagerRole,
  isRetailerRole,
  canAccessHacManagement,
  canAccessHacStaffArea,
  canViewUsers,
  canAccessLibrary,
  canAccessTranslators,
  canAccessClientPreview,
  canPublishLibraryToPre,
  canPublishLibraryToPro,
  canUploadLibraryFile,
  canCreateUser,
  canSeeReports,
  isTranslationsFeatureEnvironment,
  getHacBackPathForRole,
};
